import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Text, UnstyledButton, Loader, Center, Alert } from '@mantine/core';
import { IconBuildingStore, IconAlertCircle, IconArrowRight } from '@tabler/icons-react';
import { authApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import type { ShopResponse } from '../types';

const BG = '#09090B';
const CARD_BG = '#111113';
const CARD_BG_HOVER = '#18181B';
const BORDER = '#27272A';
const TEXT_PRIMARY = '#F4F4F5';
const TEXT_MUTED = '#71717A';
const BLUE = '#2563EB';

export function SelectShopPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [shops, setShops] = useState<ShopResponse[] | null>(null);
  const [error, setError] = useState('');
  const [switching, setSwitching] = useState<number | null>(null);

  useEffect(() => {
    authApi
      .myShops()
      .then((s) => {
        setShops(s);
        if (s.length === 0) {
          navigate('/shop/dashboard', { replace: true });
        } else if (s.length === 1 && user?.shop_id === s[0].id) {
          navigate('/shop/dashboard', { replace: true });
        }
      })
      .catch(() => {
        setError('Could not load your shops. Please try again.');
      });
  }, [navigate, user?.shop_id]);

  const pick = async (shop: ShopResponse) => {
    if (switching) return;
    setSwitching(shop.id);
    setError('');
    try {
      await authApi.switchShop(shop.id);
      await refreshUser();
      window.location.href = '/shop/dashboard';
    } catch {
      setError('Could not switch to that shop. Please try another.');
      setSwitching(null);
    }
  };

  return (
    <Box style={{ minHeight: '100vh', background: BG, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
      <Box style={{ width: '100%', maxWidth: 480 }}>
        <Stack gap={6} mb={24} align="center">
          <Text size="xl" fw={700} c={TEXT_PRIMARY}>Choose a shop</Text>
          <Text size="sm" c={TEXT_MUTED}>You belong to more than one shop. Pick the one you'd like to work in.</Text>
        </Stack>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} mb={16} variant="light">
            {error}
          </Alert>
        )}

        {shops == null ? (
          <Center mt={40}><Loader size="md" color="haist" /></Center>
        ) : (
          <Stack gap={10}>
            {shops.map((s) => {
              const isActive = user?.shop_id === s.id;
              return (
                <UnstyledButton
                  key={s.id}
                  onClick={() => pick(s)}
                  disabled={switching != null}
                  style={{
                    background: CARD_BG,
                    border: `1px solid ${isActive ? BLUE : BORDER}`,
                    borderRadius: 12,
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    transition: 'background 0.15s, border-color 0.15s',
                    opacity: switching != null && switching !== s.id ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = CARD_BG_HOVER; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = CARD_BG; }}
                >
                  <Box style={{ width: 36, height: 36, borderRadius: 8, background: '#1F1F23', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconBuildingStore size={18} color={isActive ? BLUE : TEXT_MUTED} />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} c={TEXT_PRIMARY} truncate>{s.name}</Text>
                    {s.address && (
                      <Text size="xs" c={TEXT_MUTED} truncate>{s.address}</Text>
                    )}
                  </Box>
                  {switching === s.id ? (
                    <Loader size={16} color="haist" />
                  ) : (
                    <IconArrowRight size={16} color={TEXT_MUTED} />
                  )}
                </UnstyledButton>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
